import {
  Text,
  Button,
  Stack,
  ButtonGroup,
  Card,
  Spinner,
  RadioGroup,
  Alert,
  AlertIndicator,
  AlertDescription,
  HStack,
  IconButton,
} from "@chakra-ui/react";
import { observer } from "mobx-react";
import { useStores } from "../../../../shared/stores/RootStore";
import { useEffect, useState } from "react";
import { MapboxApi } from "../../../../map/services/MapboxApi";
import { DirectionsRoute } from "../../../../map/models/Mapbox";
import {
  destinationColor,
  originColor,
} from "../../../../map/sources/QueryInfrastructureMapSource";
import { CloseIcon } from "../../../../shared/views/icons";

const QueryHlaOriginDestinationControlsBase: React.FC = () => {
  const { mapQueryStore, mapStore } = useStores();

  const [origin, setOrigin] = useState<mapboxgl.LngLat>();
  const [destination, setDestination] = useState<mapboxgl.LngLat>();
  const [routes, setRoutes] = useState<DirectionsRoute[]>([]);
  const [selectedRouteIndex, setSelectedRouteIndex] = useState(0);

  const [fetchingRoutes, setFetchingRoutes] = useState(false);

  const importRoute = async (lineString: GeoJSON.LineString) => {
    mapQueryStore.importRoute(lineString.coordinates);
  };

  const updateMapData = () => {
    mapStore.queryInfrastructureMapSource?.setOriginDestinationData(
      origin,
      destination,
      routes.map((r) => r.geometry),
      selectedRouteIndex,
    );
  };

  useEffect(() => {
    updateMapData();

    if (!origin || !destination) {
      mapStore.queryInfrastructureMapSource?.awaitPointSelection((point) => {
        if (!origin) {
          setOrigin(point);
        } else {
          setDestination(point);
        }
      });
    } else {
      const extractCoords = (lngLat: mapboxgl.LngLat): [number, number] => [
        lngLat.lng,
        lngLat.lat,
      ];

      setFetchingRoutes(true);
      MapboxApi.directions([
        extractCoords(origin!),
        extractCoords(destination!),
      ])
        .then(({ data }) => {
          setRoutes(data.routes);
          if (data.routes.length) {
            setSelectedRouteIndex(0);
          }
        })
        .finally(() => {
          setFetchingRoutes(false);
        });
    }
    // eslint-disable-next-line
  }, [origin, destination]);

  useEffect(() => {
    if (routes.length > 0) {
      updateMapData();
    }
    // eslint-disable-next-line
  }, [routes]);

  const renderAlert = (text: string) => (
    <Alert.Root status="info">
      <AlertIndicator />
      <AlertDescription>{text}</AlertDescription>
    </Alert.Root>
  );

  const PointItem = ({
    label,
    letter,
    color,
    onErase,
  }: {
    label: string;
    letter: string;
    color: string;
    onErase: () => void;
  }) => (
    <Card.Root px={2} fontSize="sm" borderColor={color}>
      <HStack fontSize="sm">
        <HStack flexGrow={1} gap={1}>
          <Text textTransform={"uppercase"}>Point</Text>
          <Text fontWeight={"bold"} color={color}>
            {letter}
          </Text>
        </HStack>
        <IconButton
          size="sm"
          onClick={() => {
            onErase();
            setRoutes([]);
          }}
          variant={"ghost"}
        >
          <CloseIcon />
        </IconButton>
      </HStack>
    </Card.Root>
  );

  return (
    <Stack gap={4}>
      {origin ? (
        <PointItem
          label="From"
          letter={"A"}
          color={originColor}
          onErase={() => setOrigin(undefined)}
        />
      ) : (
        renderAlert("Click the map to set a origin")
      )}

      {!!origin &&
        (destination ? (
          <PointItem
            label="To"
            letter={"B"}
            color={destinationColor}
            onErase={() => setDestination(undefined)}
          />
        ) : (
          renderAlert("Click the map to set a destination")
        ))}

      {fetchingRoutes && (
        <Spinner color={"primary.600"} my={4} alignSelf={"center"} />
      )}

      {routes.length > 1 && (
        <Stack fontSize="sm">
          <Text fontWeight={"semibold"}>Route options</Text>

          <RadioGroup.Root
            colorPalette="primary"
            defaultValue={"0"}
            onValueChange={({ value }) => {
              setSelectedRouteIndex(parseInt(value!));
            }}
          >
            {routes.map((route, index) => (
              <RadioGroup.Item value={index.toString()} key={index}>
                <RadioGroup.ItemHiddenInput />
                <RadioGroup.ItemIndicator />
                <RadioGroup.ItemText>
                  Option {index + 1} ({route.distance} m)
                </RadioGroup.ItemText>
              </RadioGroup.Item>
            ))}
          </RadioGroup.Root>
        </Stack>
      )}

      <ButtonGroup
        alignSelf={"end"}
        size="sm"
        colorPalette={"primary"}
        fontSize={"sm"}
      >
        <Button
          onClick={() => mapQueryStore.deactivate()}
          variant={"outline"}
          disabled={mapQueryStore.runningQuery}
        >
          Cancel
        </Button>
        <Button
          disabled={routes.length === 0}
          onClick={() => importRoute(routes[selectedRouteIndex].geometry)}
          loading={mapQueryStore.runningQuery}
        >
          Import
        </Button>
      </ButtonGroup>
    </Stack>
  );
};

export const QueryHlaOriginDestinationControls = observer(
  QueryHlaOriginDestinationControlsBase,
);
